import { Router } from "@frui.ts/screens";
import { Modules, newNavigationName } from "config";
import CreateAccountBalanceCheckRequestRequest from "entities/createAccountBalanceCheckRequestRequest";
import type { interfaces } from "inversify";
import { action, computed, observable } from "mobx";
import AccountingBalanceRepository from "repositories/accountingBalanceRepository";
import EnumService from "services/enumService";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import MonthOptionsService from "services/monthOptionsService";
import NotificationService from "services/notificationService";
import CreateViewModel from "viewModels/createViewModel";
import { attachManualValidator, addError, getValidationMessage, removeError } from "@frui.ts/validation";
import { watchBusy } from "@frui.ts/screens";

@Router.registerRoute({ route: newNavigationName, name: Router.Self })
export default class BalanceCreateViewModel extends CreateViewModel<CreateAccountBalanceCheckRequestRequest> {
  readonly modelName = "balance";
  @observable createdBalanceCheckId?: string;
  @observable loadedFromMonth?: string;
  @observable loadedToMonth?: string;

  private unsubscribtions: Array<() => void> = [];

  constructor(
    protected localizationService: LocalizationService,
    protected repository: AccountingBalanceRepository,
    protected notificationService: NotificationService,
    protected enumService: EnumService,
    protected eventBus: EventBus,
    protected monthOptionsService: MonthOptionsService,
    private toMonth?: string,
    private fromMonth?: string,
    private editId?: string
  ) {
    super();
    this.navigationName = editId ?? newNavigationName;
    if (editId) {
      this.createdBalanceCheckId = editId;
    }
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();

    attachManualValidator(this.item, true);

    if (!this.fromMonth || !this.toMonth) {
      this.loadedFromMonth = localStorage.getItem("balance_nextAvailablePeriod") ?? undefined;
      this.loadedToMonth = localStorage.getItem("balance_latestAccountingPeriodTo") ?? undefined;
    }
  }

  @computed
  get isEditMode() {
    return !!this.editId;
  }

  @computed
  get availableMonths() {
    const effectiveFromMonth = this.fromMonth ?? this.loadedFromMonth;
    const effectiveToMonth = this.toMonth ?? this.loadedToMonth;

    let minDate: Date | undefined;
    if (effectiveFromMonth) {
      const [year, month] = effectiveFromMonth.split("-").map(Number);
      minDate = new Date(year, month - 1, 1);
    }

    let maxDate: Date | undefined;
    if (effectiveToMonth) {
      const [year, month] = effectiveToMonth.split("-").map(Number);
      maxDate = new Date(year, month - 1, 1);
    }

    return this.monthOptionsService.startOfMonthOptions
      .filter(option => {
        const optionDate = option.id;
        const afterMin = minDate ? optionDate >= minDate : true;
        const beforeMax = maxDate ? optionDate <= maxDate : true;
        return afterMin && beforeMax;
      })
      .map(option => {
        const date = option.id;
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        return {
          id: `${year}-${month}`,
          label: option.label.replace(/^(\d{4}) - (\d{2})$/, "$2/$1"),
        };
      });
  }

  @computed
  get accountingPeriodToError() {
    return getValidationMessage(this.item, "accountingPeriodTo");
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscribtions.forEach(unsubsribe => unsubsribe());
      this.unsubscribtions = [];
    }

    return super.onDeactivate(isClosing);
  }

  @action.bound
  validateAccountingPeriodTo() {
    if (!this.item) {
      return false;
    }

    removeError(this.item, "accountingPeriodTo");

    if (!this.item.accountingPeriodTo) {
      addError(this.item, "accountingPeriodTo", this.localizationService.translate("validators.required"));
      return false;
    }
    return true;
  }

  @action.bound
  @watchBusy
  async onCreate(e?: React.FormEvent) {
    e?.preventDefault();

    if (!this.validateAccountingPeriodTo()) {
      return;
    }

    try {
      await this.repository.create(this.item);
      localStorage.removeItem("balance_nextAvailablePeriod");
      this.eventBus.publish({ type: `${this.modelName}.updated`, payload: {} });
      this.requestClose();
    } catch (error) {
      // Error handling is done by repository/interceptor
    }
  }

  @action.bound
  onClose() {
    this.eventBus.publish({ type: `${this.modelName}.updated`, payload: {} });
    this.requestClose();
  }

  protected postData() {
    return this.repository.create(this.item);
  }

  protected loadDetail() {
    return new CreateAccountBalanceCheckRequestRequest();
  }

  get locale() {
    return this.localizationService.locale;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.balance.create.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (toMonth?: string, fromMonth?: string, editId?: string) =>
      new BalanceCreateViewModel(
        container.get(LocalizationService),
        container.get(AccountingBalanceRepository),
        container.get(NotificationService),
        container.get(EnumService),
        container.get(EventBus),
        container.get(MonthOptionsService),
        toMonth,
        fromMonth,
        editId
      );
  }
}
